import type { ProductListing } from "@adpilot/shared";
import { IconBrandAmazon, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { fetchProductListings, resolveProductListings } from "@/lib/product-listing-api";
import { CompactEmptyState } from "./SectionLayout";
import { parseAsins } from "./sponsored-brands-format";

export function ProductsSection({
  shopId,
  maximum,
  minimum,
  input,
  onInputChange,
  asins,
  onRemove,
  onAdd,
  onAddAsins,
  isLoading,
}: {
  shopId: string;
  maximum: number;
  minimum: number;
  input: string;
  onInputChange: (value: string) => void;
  asins: string[];
  onRemove: (asin: string) => void;
  onAdd: () => void;
  onAddAsins: (asins: string[]) => Promise<void>;
  isLoading: boolean;
}) {
  const { t } = useTranslation("campaignCreation");
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState<"name" | "asin" | "sku">("name");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [page, setPage] = useState(1);
  const listings = useQuery({
    queryKey: ["sb-product-search", shopId, search, page],
    queryFn: () => fetchProductListings(shopId, undefined, { search, active: "true" }, page),
  });
  const visibleListings = [...(listings.data?.data ?? [])]
    .filter((listing) => {
      if (!search) return true;
      const needle = search.toLowerCase();
      const value =
        searchField === "name"
          ? listing.title
          : searchField === "asin"
            ? listing.asin
            : listing.sku;
      return value?.toLowerCase().includes(needle) === true;
    })
    .sort((left, right) =>
      sortOrder === "newest"
        ? right.firstSeenAt.localeCompare(left.firstSeenAt)
        : left.firstSeenAt.localeCompare(right.firstSeenAt),
    );
  const pageAsins = [
    ...new Set(visibleListings.map((listing) => listing.asin).filter(Boolean)),
  ].filter((asin) => !asins.includes(asin));
  const selectedListings = useQuery({
    queryKey: ["sb-selected-products", shopId, asins],
    queryFn: () => resolveProductListings(shopId, asins),
    enabled: asins.length > 0,
  });
  const selectedProducts = useMemo(() => {
    const listingsByAsin = new Map<string, ProductListing>();
    for (const listing of visibleListings) listingsByAsin.set(listing.asin, listing);
    for (const resolution of selectedListings.data?.results ?? []) {
      if (resolution.type === "resolved" || resolution.type === "inactive") {
        listingsByAsin.set(resolution.listing.asin, resolution.listing);
      } else if (resolution.type === "ambiguous") {
        const listing =
          resolution.candidates.find((candidate) => candidate.asin === resolution.identifier) ??
          resolution.candidates[0];
        if (listing) listingsByAsin.set(listing.asin, listing);
      }
    }
    return asins.map((asin) => ({ asin, listing: listingsByAsin.get(asin) }));
  }, [asins, selectedListings.data?.results, visibleListings]);
  return (
    <Card data-slot="sb-products-section" className="w-full max-w-[960px] self-center">
      <CardHeader>
        <h3 className="flex items-center gap-2.5">
          <span aria-hidden="true" className="h-5 w-1 rounded-full bg-primary" />
          <CardTitle>{t("sb.products.title")}</CardTitle>
        </h3>
      </CardHeader>
      <CardContent>
        <div
          data-slot="sb-product-picker"
          className="grid h-[520px] max-h-[520px] grid-cols-2 overflow-hidden rounded-lg border border-border"
        >
          <section
            data-pane="available-products"
            aria-label={t("sb.products.availablePane")}
            className="flex min-h-0 min-w-0 flex-col border-r border-border"
          >
            <Tabs defaultValue="search" className="min-h-0 flex-1 gap-0">
              <TabsList className="w-full justify-start px-4">
                <TabsTrigger value="search">{t("sb.products.search")}</TabsTrigger>
                <TabsTrigger value="enter">{t("sb.products.enter")}</TabsTrigger>
              </TabsList>
              <TabsContent value="search" className="flex min-h-0 flex-col gap-3 p-4">
                <div className="grid grid-cols-[112px_1fr_132px] gap-2">
                  <Select
                    items={[
                      { value: "name", label: t("sb.products.searchField.name") },
                      { value: "asin", label: t("sb.products.searchField.asin") },
                      { value: "sku", label: t("sb.products.searchField.sku") },
                    ]}
                    value={searchField}
                    onValueChange={(value) => {
                      if (value === "name" || value === "asin" || value === "sku") {
                        setSearchField(value);
                        setPage(1);
                      }
                    }}
                  >
                    <SelectTrigger aria-label={t("sb.products.searchField.label")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="name">{t("sb.products.searchField.name")}</SelectItem>
                        <SelectItem value="asin">{t("sb.products.searchField.asin")}</SelectItem>
                        <SelectItem value="sku">{t("sb.products.searchField.sku")}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Input
                    aria-label={t("sb.products.searchInput")}
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder={t("sb.products.searchPlaceholder")}
                  />
                  <Select
                    items={[
                      { value: "newest", label: t("sb.products.sort.newest") },
                      { value: "oldest", label: t("sb.products.sort.oldest") },
                      { value: "preferred", label: t("sb.products.sort.preferred") },
                    ]}
                    value={sortOrder}
                    onValueChange={(value) => {
                      if (value === "newest" || value === "oldest") setSortOrder(value);
                    }}
                  >
                    <SelectTrigger aria-label={t("sb.products.sort.label")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="newest">{t("sb.products.sort.newest")}</SelectItem>
                        <SelectItem value="oldest">{t("sb.products.sort.oldest")}</SelectItem>
                        <SelectItem value="preferred" disabled>
                          {t("sb.products.sort.preferred")}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t("sb.products.title")}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={!pageAsins.length || isLoading || asins.length >= maximum}
                    onClick={() => void onAddAsins(pageAsins.slice(0, maximum - asins.length))}
                  >
                    {t("sb.products.addPage")}
                  </Button>
                </div>
                <ScrollArea
                  className="min-h-0 flex-1 rounded-md border border-border"
                  scrollbarGutter
                >
                  <div className="divide-y divide-border">
                    {visibleListings.map((listing) => (
                      <SbProductCard
                        key={listing.listingKey}
                        listing={listing}
                        action={
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={
                              asins.includes(listing.asin) || isLoading || asins.length >= maximum
                            }
                            onClick={() => void onAddAsins([listing.asin])}
                          >
                            {t("sb.actions.add")}
                          </Button>
                        }
                      />
                    ))}
                    {!listings.isLoading && !visibleListings.length ? <CompactEmptyState /> : null}
                    {listings.data && listings.data.pagination.total > 0 ? (
                      <div className="flex items-center justify-between p-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={page === 1}
                          onClick={() => setPage((current) => Math.max(1, current - 1))}
                        >
                          {t("sb.products.previousPage")}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {t("sb.products.pageStatus", {
                            page,
                            total: Math.max(1, Math.ceil(listings.data.pagination.total / 20)),
                          })}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!listings.data.pagination.hasMore}
                          onClick={() => setPage((current) => current + 1)}
                        >
                          {t("sb.products.nextPage")}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </ScrollArea>
              </TabsContent>
              <TabsContent value="enter" className="min-h-0 overflow-y-auto p-4">
                <Field>
                  <FieldLabel>{t("sb.products.enterAsins")}</FieldLabel>
                  <Textarea
                    aria-label={t("sb.products.enterAsins")}
                    placeholder={t("sb.products.asinPlaceholder")}
                    value={input}
                    onChange={(event) => onInputChange(event.target.value)}
                  />
                  <FieldDescription>{t("sb.products.eligibilityDescription")}</FieldDescription>
                </Field>
                <Button
                  type="button"
                  className="mt-3"
                  disabled={!parseAsins(input).length || isLoading || asins.length >= maximum}
                  onClick={onAdd}
                >
                  {isLoading ? t("sb.products.checking") : t("sb.actions.add")}
                </Button>
              </TabsContent>
            </Tabs>
          </section>
          <section
            data-pane="added-products"
            aria-label={t("sb.products.addedPane")}
            className="flex min-h-0 min-w-0 flex-col"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <p className="font-medium">{t("sb.common.added", { count: asins.length })}</p>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={asins.length === 0}
                onClick={() => asins.forEach(onRemove)}
              >
                {t("sb.common.removeAll")}
              </Button>
            </div>
            <div className="border-b border-border px-4 py-3">
              <p className="text-sm font-medium">{t("sb.products.title")}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t(maximum === 1 ? "sb.products.maximumOne" : "sb.products.minimumThree")} (
                {asins.length}/{maximum})
              </p>
              {asins.length > 0 && asins.length < minimum ? (
                <p className="mt-1 text-sm text-warning-foreground">
                  {t("sb.products.needMore", { count: minimum - asins.length })}
                </p>
              ) : null}
            </div>
            <ScrollArea className="min-h-0 flex-1" scrollbarGutter>
              {selectedProducts.length ? (
                <div className="divide-y divide-border">
                  {selectedProducts.map(({ asin, listing }) => (
                    <SbProductCard
                      key={asin}
                      asin={asin}
                      listing={listing}
                      action={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t("sb.common.remove", { name: asin })}
                          onClick={() => onRemove(asin)}
                        >
                          <IconTrash />
                        </Button>
                      }
                    />
                  ))}
                </div>
              ) : (
                <CompactEmptyState />
              )}
            </ScrollArea>
          </section>
        </div>
      </CardContent>
    </Card>
  );
}

function SbProductCard({
  asin,
  listing,
  action,
}: {
  asin?: string;
  listing?: ProductListing;
  action: ReactNode;
}) {
  const { t } = useTranslation("campaignCreation");
  const productAsin = listing?.asin ?? asin ?? "";
  const availability =
    listing?.availability === "IN_STOCK"
      ? t("sb.products.availability.inStock")
      : listing?.availability === "IN_STOCK_SCARCE"
        ? t("sb.products.availability.scarce")
        : listing?.availability === "OUT_OF_STOCK"
          ? t("sb.products.availability.outOfStock")
          : t("sb.products.availability.unknown");
  const currency = listing?.priceCurrency === "USD" ? "$" : listing?.priceCurrency;
  const pricePrefix = currency === "$" ? currency : currency ? `${currency} ` : "";
  return (
    <article className="flex min-w-0 items-center gap-3 p-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {listing?.imageUrl ? (
          <img src={listing.imageUrl} alt="" className="size-14 shrink-0 rounded object-contain" />
        ) : (
          <div className="flex size-14 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
            <IconBrandAmazon aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate font-medium">{listing?.title ?? productAsin}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {listing?.priceAmount
              ? `${pricePrefix}${listing.priceAmount}`
              : t("sb.products.priceUnavailable")}
            <span className="px-1.5">|</span>
            {availability}
          </p>
          <p className="mt-1 flex min-w-0 items-center text-xs text-muted-foreground">
            <span className="shrink-0 font-mono">ASIN: {productAsin}</span>
            {listing?.sku ? (
              <>
                <span className="px-1.5">|</span>
                <span className="truncate font-mono">SKU: {listing.sku}</span>
              </>
            ) : null}
          </p>
        </div>
      </div>
      <div className="shrink-0">{action}</div>
    </article>
  );
}
